/* eslint-disable no-nested-ternary */
import { useQuery } from '@apollo/client';
import { notification, Tooltip } from 'antd';
import { ColumnsType } from 'antd/lib/table/interface';
import BaseStructure from 'components/BaseStructure';
import AvatarGroup from 'components/common/AvatarGroup';
import DefaultModal from 'components/common/DefaultModal';
import IconComponent from 'components/common/IconComponent';
import SearchBarSuggestion from 'components/common/SearchBarSuggestion';
import PageWithTableContainer from 'components/PageWithTableContainer/PageWithTableContainer';
import { FORMAT_DATE_TIME_FOR_DISPLAY } from 'constants/days';
import deviceSizes from 'constants/deviceSizes';
import { NOTIFY } from 'constants/notify';
import useCurrentLoggedInUser from 'hooks/apollo/queries/useCurrentLoggedInUser';
import { useManyDataQuery } from 'hooks/useFastGqlQuery';
import useReactiveQueryTable from 'hooks/useReactiveQueryTable';
import { useRoutesDisplayValues } from 'hooks/useRoutesDisplayValues';
import { flatten } from 'lodash';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { INotification } from 'redux/notification/types';
import { SearchScopeEnum } from 'redux/search/types';
import ROUTES from 'routes/routeConstants';
import { TextOnlyButtonStyled } from 'routes/Transaction/components/styled';
import { GET_COMPANY_LOCATION } from 'services/graphql/queries/location.query';
import { GET_TRANSACTIONS } from 'services/graphql/queries/transactions.query';
import { GET_USER_SERVICE_TYPE_AND_LOCATIONS } from 'services/graphql/queries/user.query';
import { IAppointment } from 'types/appointments';
import { ITransactionType } from 'types/services';
import { ITransaction, TransactionStatusEnum } from 'types/transaction';
import { CompanyRoleEnum, IUser, RoleEnum } from 'types/user';
import { fastGetDefaultAvatarUrl, formatCurrency, formatListNames, getStatusColor } from 'utils';
import { checkRealtimeUpdate, notifyEvent } from 'utils/realtimeUpdate';

import ClientAssignTable from './components/ClientAssignTable';
import CloneTransactionButton from './components/CloneTransactionButton';
import CreateTransactionButtonWithTransactionModal from './components/CreateTransactionButtonWithTransactionModal';
import SearchAndFilterBar from './components/SearchAndFilterBar';
import TransactionList from './components/TransactionList';
import { ITransactionListTableProps } from './components/TransactionList/types';
import { RefNoStyled } from './styled';

const Transactions: React.FC = () => {
  const { state } = useLocation();
  const history = useHistory();
  const [selectedTransaction, setSelectedTransaction] = useState<ITransaction>();
  const { role, id, companyRole, mainLocation } = useCurrentLoggedInUser(); // test nha
  const isAdmin = role === RoleEnum.ADMIN;
  const isClient = role === RoleEnum.CLIENT;
  const isCompanyAdmin = companyRole === CompanyRoleEnum.ADMIN;

  const { data: userData, loading: userLoading } = useQuery<{ findOneUserEntity: IUser }>(
    GET_USER_SERVICE_TYPE_AND_LOCATIONS,
    {
      variables: {
        id,
        user: id,
      },
    },
  );

  const user = userData?.findOneUserEntity;
  const serviceTypesData = user?.company?.serviceTypes;
  const transactionTypes = serviceTypesData?.reduce(
    (acc: ITransactionType[], service) => acc.concat(service.transactionTypes),
    [],
  );

  const { content: locationData, loading: locationLoading } = useManyDataQuery(GET_COMPANY_LOCATION, {
    variables: {
      pagination: { take: 50 },
    },
    skip: !isCompanyAdmin,
  });

  const locations = isCompanyAdmin ? locationData : user?.usersLocations?.map((uL) => uL.location);

  const originalColumns: ColumnsType<ITransactionListTableProps> = useMemo(
    () => [
      {
        title: 'Ref. No.',
        dataIndex: 'reference',
        sorter: true,
        render: (text: string, record: ITransactionListTableProps) => (
          <div>
            <RefNoStyled>{text}</RefNoStyled>
            {record.status === TransactionStatusEnum.CANCELLED && !isAdmin && (
              <CloneTransactionButton cloneTransaction={record.transaction} />
            )}
          </div>
        ),
        // sorter: (a: ITransactionListTableProps, b: ITransactionListTableProps) => a.reference.localeCompare(b.reference),
      },
      {
        title: 'Description',
        dataIndex: 'description',
        render: (text: string) => (
          <div>
            <span>{text}</span>
          </div>
        ),
        // sorter: (a: ITransactionListTableProps, b: ITransactionListTableProps) => a.lastName.localeCompare(b.lastName),
      },
      {
        title: '# Appts',
        dataIndex: 'appointments',
        // sorter: true,
        render: (appointments: IAppointment[]) => <div className='sm:pl-3'>{appointments.length}</div>,
      },
      {
        title: 'Fee',
        dataIndex: 'fee',
        sorter: true,
        // sorter: (a: ITransactionListTableProps, b: ITransactionListTableProps) => a.lastName.localeCompare(b.lastName),
      },
      {
        title: 'Created By',
        dataIndex: 'createdBy',
        sorter: true,
      },
      {
        title: 'Assigned To',
        dataIndex: 'assigners',
        sorter: false,
        render: (assigners: IUser[], record: ITransactionListTableProps) => (
          <div className='gx-d-flex gx-align-items-center'>
            {assigners.length > 1 ? (
              <Tooltip
                title={formatListNames(assigners.map((assigner) => `${assigner.firstName} ${assigner.lastName}`))}
                placement='top'>
                <div>
                  <AvatarGroup
                    sources={[...assigners]
                      .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`))
                      .map((assigner) => fastGetDefaultAvatarUrl(assigner))}
                  />
                </div>
              </Tooltip>
            ) : assigners.length === 1 ? (
              <div>
                {assigners[0].firstName} {assigners[0].lastName}
              </div>
            ) : (
              <div>None</div>
            )}
            {role === RoleEnum.CLIENT && (
              <TextOnlyButtonStyled
                className='gx-ml-2 assign-client-button'
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedTransaction(record.transaction);
                }}>
                <Tooltip title='Assign Transaction'>
                  <IconComponent color='#038fde' name='ckeditor' />
                </Tooltip>
              </TextOnlyButtonStyled>
            )}
          </div>
        ),
      },
      // {
      //   title: 'Notary Name',
      //   dataIndex: 'notary',
      //   sorter: true,
      //   // sorter: (a: ITransactionListTableProps, b: ITransactionListTableProps) => a.email.localeCompare(b.email),
      // },
      // {
      //   title: 'Notary Appointments',
      //   dataIndex: 'date',
      //   // sorter: (a: ITransactionListTableProps, b: ITransactionListTableProps) => a.email.localeCompare(b.email),
      // },
      {
        title: 'Status',
        dataIndex: 'status',
        sorter: true,
        render: (text: TransactionStatusEnum) => <span style={{ color: getStatusColor(text) }}>{text}</span>,
        // filters: [
        //   { text: USER_STATUS.ACTIVE, value: USER_STATUS.ACTIVE },
        //   { text: USER_STATUS.SENT, value: USER_STATUS.SENT },
        //   { text: USER_STATUS.DISABLED, value: USER_STATUS.DISABLED },
        // ],
      },
    ],
    [id, role, companyRole],
  );

  // const mobileConfig: IMobileTableConfig<ITransactionListTableProps> = useMemo(
  //   () => ({
  //     grid: [
  //       ['reference', 'fee', 'appointments'],
  //       ['createdBy', { value: 'description', span: undefined, flex: 'auto' }],
  //       [{ value: 'status', span: undefined, flex: 'auto' }],
  //     ],
  //   }),
  //   [role],
  // );

  // Main Row Transaction Table
  const dataMapper = React.useCallback((t: ITransaction): ITransactionListTableProps => {
    const numberOfSignatories = t.signatories.length;
    const firstSignatory = t.signatories[0];
    const firstSignatoryName = firstSignatory ? `${firstSignatory.firstName} ${firstSignatory.lastName}` : '';
    const schedules = t.appointment ? flatten([t.appointment]) : [];
    let dateText = 'None';
    if (schedules && schedules.length > 0) {
      dateText =
        schedules.length > 1
          ? `${moment(schedules[0].date).format(FORMAT_DATE_TIME_FOR_DISPLAY)} and ${schedules.length - 1} more`
          : moment(schedules[0].date).format(FORMAT_DATE_TIME_FOR_DISPLAY);
    }
    // let signatoryText = '0';
    // if (numberOfSignatories === 1) {
    //   signatoryText = firstSignatoryName;
    // } else if (numberOfSignatories > 1) {
    //   signatoryText = `${firstSignatoryName} & ${numberOfSignatories - 1} more`;
    // }
    const fee = t?.clientFee;
    const isFeeFree = fee === 0;
    const isFeeSet = !!fee;
    let feeValue = fee ? `${formatCurrency(fee)}` : 0;
    if (!isFeeSet) {
      feeValue = 'Not Set';
    }
    if (isFeeFree) {
      feeValue = '——';
    }

    const creator = t.createdBy;
    const assignedUsers = t.assigners;

    return {
      reference: t.reference,
      description: t.description,
      key: t.id,
      appointments: t.appointment || [],
      fee: feeValue,
      createdBy: creator?.firstName ? `${creator.firstName} ${creator.lastName}` : 'None',
      assigners: assignedUsers || [],
      date: dateText,
      status: t.status,
      signatories: { firstSigName: firstSignatoryName, countLeft: numberOfSignatories - 1 },
      transaction: t,
    };
  }, []);

  const { refetch: refetchTransactionList, ...tableProps } = useReactiveQueryTable({
    graphQLQuery: GET_TRANSACTIONS,
    columns: originalColumns,
    dataMapper,
    mapQueryToVariables: (queryObject) => ({
      query: queryObject?.query,
      withoutNotary: queryObject?.withoutNotary,
      locationIds: mainLocation?.id && isClient ? mainLocation.id : undefined,
      onlyMine: queryObject?.onlyMine,
    }),
    queryProps: {
      fetchPolicy: 'no-cache',
    },
  });

  // change main location = change table data query => refetch data
  useEffect(() => {
    if (isClient) {
      refetchTransactionList();
    }
  }, [mainLocation]);

  const refetchTransactionListWithUserId = () => {
    refetchTransactionList();
  };

  useEffect(() => {
    if (state) {
      history.replace({ ...history.location, state: undefined });
    }
  }, []);

  useEffect(() => {
    if (state === `transaction-created`) {
      refetchTransactionList();
      notification.success({
        message: 'Transaction created successfully!',
        duration: 2,
      });
    } else if (state === `transaction-edited`) {
      refetchTransactionList();
      notification.success({
        message: 'Transaction edited successfully!',
        duration: 2,
      });
    }
  }, [state]);

  const routeDisplayValue = useRoutesDisplayValues()[ROUTES.TRANSACTIONS];

  const handleSubscribeNotify = (subData: { newNotification: INotification }) => {
    if (checkRealtimeUpdate(subData.newNotification.subType)) {
      refetchTransactionList();
    }
  };

  useEffect(() => {
    notifyEvent.addListener(NOTIFY.NEW, handleSubscribeNotify);
    return () => {
      notifyEvent.removeListener(NOTIFY.NEW, handleSubscribeNotify);
    };
  }, []);

  const closeAssignTransactionModal = () => {
    setSelectedTransaction(undefined);
  };

  const onAssignTransactionSuccess = () => {
    refetchTransactionList();
  };

  return (
    <>
      <DefaultModal
        props={{
          width: 800,
          style: { maxWidth: '100%', padding: 0 },
          visible: !!selectedTransaction,
          title: 'Assign Transaction',
          onCancel: closeAssignTransactionModal,
        }}>
        <ClientAssignTable transaction={selectedTransaction} callbackOnAssignSuccess={onAssignTransactionSuccess} />
      </DefaultModal>
      <BaseStructure
        mobileThreshold={575}
        header={routeDisplayValue.text}
        headerIconName={routeDisplayValue.iconClass}
        headerButton={
          isAdmin ? undefined : (
            <CreateTransactionButtonWithTransactionModal
              loading={userLoading || locationLoading}
              transactionTypes={transactionTypes}
              locations={locations}
            />
          )
        }
        headerSide={
          <div className='gx-d-inline-block w-full' style={{ maxWidth: 400 }}>
            <SearchBarSuggestion
              placeholder='Search by Refs, Descriptions, Signatories'
              entities={[SearchScopeEnum.TRANSACTION]}
            />
          </div>
        }
        headerStyle={{ flexWrap: 'wrap' }}
        headerBelow={<SearchAndFilterBar locations={locations} />}>
        <PageWithTableContainer>
          <TransactionList
            {...tableProps}
            // mobileConfig={mobileConfig}
            widthThreshold={deviceSizes.numbers.sm}
            refetchTransactionList={refetchTransactionListWithUserId}
          />
        </PageWithTableContainer>
      </BaseStructure>
    </>
  );
};

export default React.memo(Transactions);
